import api from "@/api";
import { map as Rmap, find as Rfind, propEq as RpropEq } from "ramda";
import { ordinalize } from "@/utilities/ordinalize";
import { JSConn } from "@/utilities/js-conn";

const apiParamsBuilder = (jsConn: JSConn) => {
  return {
    id: jsConn.formParams.id,
    work_order_task: {
      ...jsConn.formParams,
    },
  };
};

const itemDetails = {
  pluralName: "work_order_tasks",
  singularName: "work_order_task",
};

const createWorkOrderTaskDetails = {
  pluralName: "create_work_order_tasks",
  singularName: "create_work_order_task",
};

const completedWorkOrderTaskDetails = {
  pluralName: "completed_work_order_tasks",
  singularName: "completed_work_order_task",
};

export interface CWorkOrderTaskLineItem {
  id: string;
  rate: number;
  quantity: number;
  item_date: string;
  sort_order: number;
  line_item_id: string;
  line_item: string;
}

export interface CWorkOrderTask {
  id: string;
  name: string;
  description: string;
  start_date: string;
  completed_date: string;
  status: string;
  sort_order: number;
  user_id: string;
  work_order_id: string;
  work_order_task_line_items: CWorkOrderTaskLineItem[];
}

export interface WorkOrderTask {
  id: string;
  client_field: string;
  client_field_description: string;
  client: string;
  commodity: string;
  commodity_color: string;
  name: string;
  work_order_task_id: string;
  description: string;
  scheduled_date: string;
  start_date: string;
  completed_date: null | string;
  status: string;
  sort_order: number;
  work_order_id: string;
  work_order_name: string;
  work_order_cut_number: number;
}

interface WorkOrderState {
  workOrderTasks: WorkOrderTask[];
  workOrderTask: WorkOrderTask;
  workOrderTasksByClient: ClientAndTasks[];
}

interface ClientsWithTasks {
  [clientId: string]: WorkOrderTask[];
}

export interface ClientAndTasks {
  client: string;
  tasks: WorkOrderTask[];
}

const state = <WorkOrderState>{
  workOrderTasks: [],
  workOrderTask: {
    id: "",
    client_field: "",
    client_field_description: "",
    client: "",
    commodity: "",
    commodity_color: "",
    name: "",
    work_order_task_id: "",
    description: "",
    scheduled_date: "",
    start_date: "",
    completed_date: null,
    status: "",
    sort_order: 0,
    work_order_id: "",
    work_order_name: "",
    work_order_cut_number: 0,
  },
  workOrderTasksByClient: [],
};

const mutations = {
  LOAD_WORK_ORDER_TASK(state: WorkOrderState, payload: WorkOrderTask) {
    state.workOrderTask = payload;
  },
  LOAD_WORK_ORDER_TASKS(state: WorkOrderState, payload: WorkOrderTask[]) {
    state.workOrderTasks = payload;
  },
  LOAD_WORK_ORDER_TASKS_BY_CLIENT(state: WorkOrderState, payload: any[]) {
    state.workOrderTasksByClient = payload;
  },
};

const actions = {
  needWorkOrderTasks(context: any, jsConn: JSConn) {
    jsConn.apiParams = jsConn.formParams;
    return api.getItems(jsConn, itemDetails).then((jsConn: JSConn) => {
      const apiTasks: WorkOrderTask[] = jsConn.response as WorkOrderTask[];
      context.commit("LOAD_WORK_ORDER_TASKS", apiTasks);
      // 2019-09-25
      // If this is what the client wants, most of this could be pushed
      // back to the db layer on the api
      // there isn't enough data at this point to make a lot of difference,
      // but doing this in code this way probably isn't going to provide the most performance
      // group the tasks by client
      const uniqueClients = apiTasks.reduce(
        (accum: string[], item: WorkOrderTask) => {
          if (accum.includes(item.client)) {
            return accum;
          } else {
            accum.push(item.client);
            return accum;
          }
        },
        [],
      );

      // okay now make the data structure
      const clientsWithTasks = uniqueClients.reduce(
        (accum: ClientsWithTasks, item: string): ClientsWithTasks => {
          return {
            [item]: [],
            ...accum,
          };
        },
        {},
      );

      // classify the data
      const classifiedData: ClientsWithTasks = apiTasks.reduce(
        (accum: ClientsWithTasks, item: WorkOrderTask): ClientsWithTasks => {
          accum[item.client].push(item);
          return accum;
        },
        clientsWithTasks,
      );

      // make the data structure
      const ds = uniqueClients.reduce((accum: ClientAndTasks[], item) => {
        const tasks = classifiedData[item];
        return [
          ...accum,
          {
            client: item,
            tasks: tasks,
          },
        ];
      }, []);

      context.commit("LOAD_WORK_ORDER_TASKS_BY_CLIENT", ds);
      return Promise.resolve(jsConn);
    });
  },
  needCompletedWorkOrderTasks(context: any, jsConn: JSConn) {
    jsConn.apiParams = jsConn.formParams;
    return api
      .getItems(jsConn, completedWorkOrderTaskDetails)
      .then((jsConn: JSConn) => {
        context.commit("LOAD_WORK_ORDER_TASKS", jsConn.response);
        return Promise.resolve(jsConn);
      });
  },
  needWorkOrderTask(context: any, jsConn: JSConn) {
    jsConn.apiParams = jsConn.formParams;
    return api.getItem(jsConn, itemDetails).then((jsConn: JSConn) => {
      context.commit("LOAD_WORK_ORDER_TASK", jsConn.response);
      return Promise.resolve(jsConn);
    });
  },
  deleteWorkOrderTask(_context: any, jsConn: JSConn) {
    // set the api params to be correct
    // this may be called from multiple places
    // as of 2019-03-22 it is just called from the
    // workorder show page and there the entire task
    // is being set as the form params
    jsConn.apiParams = apiParamsBuilder(jsConn);
    return api
      .deleteItem(jsConn, itemDetails)
      .then((jsConn: JSConn) => {
        return Promise.resolve(jsConn);
      })
      .catch((jsConn: JSConn) => {
        return Promise.reject(jsConn);
      });
  },
  createWorkOrderTaskFromWorkOrderTask(context: any, jsConn: JSConn) {
    jsConn.apiParams = {
      create_work_order_task: {
        ...jsConn.formParams,
      },
    };
    return api
      .createItem(jsConn, createWorkOrderTaskDetails)
      .then((jsConn: JSConn) => {
        context.commit("LOAD_WORK_ORDER_TASK", jsConn.response);
        return Promise.resolve(jsConn);
      })
      .catch((jsConn: JSConn) => {
        return Promise.reject(jsConn);
      });
  },
  createWorkOrderTask(context: any, jsConn: JSConn) {
    jsConn.apiParams = apiParamsBuilder(jsConn);
    return api
      .createItem(jsConn, itemDetails)
      .then((jsConn: JSConn) => {
        context.commit("LOAD_WORK_ORDER_TASK", jsConn.response);
        return Promise.resolve(jsConn);
      })
      .catch((jsConn: JSConn) => {
        return Promise.reject(jsConn);
      });
  },
  updateWorkOrderTask(_context: any, jsConn: JSConn) {
    jsConn.apiParams = jsConn.formParams;

    return api
      .updateItem(jsConn, itemDetails)
      .then((jsConn: JSConn) => {
        return Promise.resolve(jsConn);
      })
      .catch((jsConn: JSConn) => {
        return Promise.reject(jsConn);
      });
  },
  completeWorkOrderTask(_context: any, jsConn: JSConn) {
    jsConn.apiParams = jsConn.formParams;

    return api
      .updateItem(jsConn, completedWorkOrderTaskDetails)
      .then((jsConn: JSConn) => {
        return Promise.resolve(jsConn);
      })
      .catch((jsConn: JSConn) => {
        return Promise.reject(jsConn);
      });
  },
};

const getters = {
  workOrderTask: (state: WorkOrderState) => state.workOrderTask,

  workOrderTasks: (state: WorkOrderState) => state.workOrderTasks,

  workOrderTasksGroupedByClient: (state: WorkOrderState) =>
    state.workOrderTasksByClient,

  workOrderTasksCount: (state: WorkOrderState) => state.workOrderTasks.length,

  workOrderTaskFirst: (state: WorkOrderState) => state.workOrderTasks[0],

  workOrderTasksForTable: (state: WorkOrderState) => {
    return Rmap(task => {
      let fieldNameWithCutting = "";

      if (task.work_order_cut_number === 0) {
        fieldNameWithCutting = `${task.client_field}, ${task.commodity}`;
      } else {
        fieldNameWithCutting = `${task.client_field}, ${
          task.commodity
        }, ${ordinalize(task.work_order_cut_number)} cutting`;
      }

      return {
        ...task,
        field_name_with_cutting: fieldNameWithCutting,
      };
    }, state.workOrderTasks);
  },

  workOrderTaskById: (state: WorkOrderState) => (id: string) => {
    return Rfind(task => {
      return RpropEq("id", id, task);
    }, state.workOrderTasks);
  },

  // 2019-09-24
  // These functions for next and previous are used for the field workers
  // work order view. They allow the UI to only show one task and the user
  // can move forward and backward through the tasks
  workOrderPreviousTaskId: (state: WorkOrderState) => (id: string) => {
    // this expects to get an id of a work order task and then returns the previous
    // tasks id number
    // it is used to make a previous button for the field workers show page
    let previousId = "";

    for (let i = 0; i < state.workOrderTasks.length; i++) {
      if (state.workOrderTasks[i].id === id) {
        if (i === 0) {
          previousId = "";
        } else {
          previousId = state.workOrderTasks[i - 1].id;
        }
      }
    }

    return previousId;
  },
  workOrderNextTaskId: (state: WorkOrderState) => (id: string) => {
    // this expects to get an id of a work order task and then returns the next
    // tasks id number
    // it is used to make a next button for the field workers show page
    let nextId = "";

    for (let i = 0; i < state.workOrderTasks.length; i++) {
      if (state.workOrderTasks[i].id === id) {
        if (i === state.workOrderTasks.length - 1) {
          nextId = "";
        } else {
          nextId = state.workOrderTasks[i + 1].id;
        }
      }
    }

    return nextId;
  },
};

const workOrderTasksModule = {
  state,
  mutations,
  actions,
  getters,
};

export default workOrderTasksModule;
